// Pure decisions for the moving storm glyph and its time chips (no Leaflet / React).

// A tick longer than this (a scrub or a wrap) never glides.
export const MAX_GLIDE_STEP_MS = 6 * 3600 * 1000;

/**
 * True only for an ordinary playback tick: playing, motion allowed, a short forward step, and no explicit
 * scrub (slider drag / marker click) on this update. Everything else snaps.
 */
export function shouldGlide({ isPlaying, reducedMotion, prevMs, nextMs, scrubbed = false }) {
  if (!isPlaying || reducedMotion || scrubbed) return false;
  if (!Number.isFinite(prevMs) || !Number.isFinite(nextMs)) return false;
  const step = nextMs - prevMs;
  return step > 0 && step <= MAX_GLIDE_STEP_MS;
}

/** Pauses (and rewinds) or resumes the SVG animations of a glyph element. Safe on elements without the API. */
export function setSvgMotion(svg, reduced) {
  if (!svg) return;
  if (reduced) {
    svg.setCurrentTime?.(0);
    svg.pauseAnimations?.();
  } else {
    svg.unpauseAnimations?.();
  }
}

/** Segment [ax,ay]-[bx,by] versus rectangle [x,y,w,h] (touching counts). Liang-Barsky, allocation free. */
export function segmentHitsRect(ax, ay, bx, by, rect) {
  const [x, y, w, h] = rect;
  const dx = bx - ax;
  const dy = by - ay;
  let t0 = 0;
  let t1 = 1;
  const edges = [[-dx, ax - x], [dx, x + w - ax], [-dy, ay - y], [dy, y + h - ay]];
  for (let i = 0; i < 4; i += 1) {
    const p = edges[i][0];
    const q = edges[i][1];
    if (p === 0) {
      if (q < 0) return false;
    } else {
      const r = q / p;
      if (p < 0) { if (r > t1) return false; if (r > t0) t0 = r; }
      else { if (r < t0) return false; if (r < t1) t1 = r; }
    }
  }
  return true;
}

/** Keeps only the segments ([ax,ay,bx,by]) that touch the rectangle: offscreen geometry never costs anything. */
export const visibleSegments = (segs, rect) => segs.filter(([ax, ay, bx, by]) => segmentHitsRect(ax, ay, bx, by, rect));

export const rectInside = (r, view) => r[0] >= view[0] && r[1] >= view[1]
  && r[0] + r[2] <= view[0] + view[2] && r[1] + r[3] <= view[1] + view[3];

export const rectsOverlap = (a, b) => a[0] < b[0] + b[2] && b[0] < a[0] + a[2] && a[1] < b[1] + b[3] && b[1] < a[1] + a[3];

const grow = (r, m) => [r[0] - m, r[1] - m, r[2] + 2 * m, r[3] + 2 * m];

/** True when the candidate chip rectangle is free of placed chips, obstacle rects and track segments. */
export function isClear(rect, { placed, obstacles, segments, margin = 3 }) {
  if (placed.some((r) => rectsOverlap(r, rect))) return false;
  if (obstacles.some((o) => rectsOverlap(rect, o))) return false;
  const box = grow(rect, margin);
  return !segments.some(([ax, ay, bx, by]) => segmentHitsRect(ax, ay, bx, by, box));
}
